import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../../axiosInstance';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GenericCard from '../../../common/GenericCard';
import CommonModal from '../../../common/CommonModal';
import TextField from 'apollo-react/components/TextField';
import { normalizeWorkflowLicenseMode } from '../../../../utils/workflowLicenseMode';

const EMIRATES_ID_DIGITS = /^\d{15}$/;
const STUDY_NAME_MIN_LEN = 2;
const STUDY_NAME_MAX_LEN = 255;

function workflowModeFromItem(item) {
  return normalizeWorkflowLicenseMode(
    item?.workflow_license_mode,
    item?.license_renewal
  );
}

const NewRequests = () => {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [showRequestNameModal, setShowRequestNameModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [customRequestName, setCustomRequestName] = useState('');
  const [requestNameError, setRequestNameError] = useState('');
  const [emiratesGateReason, setEmiratesGateReason] = useState(null);
  const [emiratesGateRequestId, setEmiratesGateRequestId] = useState(null);
  const [lastEmiratesDigits, setLastEmiratesDigits] = useState('');
  const [checkingEmiratesId, setCheckingEmiratesId] = useState(false);
  /** 'normal' | 'new_license' | 'license_renewal' */
  const [selectedWorkflowLicenseMode, setSelectedWorkflowLicenseMode] =
    useState('new_license');

  useEffect(() => {
    const fetchNewRequests = async () => {
      const response = await axiosInstance
        .get('/api/requests-progress/user/requests/new')
        .catch((err) => {
          console.log(err);
        });
      setData(response?.data);
    };
    fetchNewRequests();
  }, []);

  const renewalWorkflowVersionId = useMemo(() => {
    const w = data?.find(
      (item) => workflowModeFromItem(item) === 'license_renewal'
    );
    return w?.id != null ? String(w.id) : '';
  }, [data]);

  const handleApply = (id) => {
    const item = data?.find((w) => w.id === id);
    setSelectedRequestId(id);
    setSelectedWorkflowLicenseMode(workflowModeFromItem(item));
    setShowRequestNameModal(true);
    setCustomRequestName('');
    setRequestNameError('');
    setEmiratesGateReason(null);
    setEmiratesGateRequestId(null);
    setLastEmiratesDigits('');
  };

  const gateHelperText = () => {
    if (selectedWorkflowLicenseMode === 'normal') {
      return requestNameError || '';
    }
    if (!emiratesGateReason) {
      return requestNameError || '';
    }
    if (emiratesGateReason === 'completed_license') {
      return t('Emirates ID license already exists');
    }
    if (emiratesGateReason === 'open_existing') {
      return t('Emirates ID open request exists');
    }
    if (emiratesGateReason === 'under_review') {
      return t('Emirates ID under review');
    }
    if (emiratesGateReason === 'renew_in_progress') {
      return t('Emirates ID renew not finished');
    }
    if (emiratesGateReason === 'renew_no_completed') {
      return t('Emirates ID no license to renew');
    }
    return t('Could not verify Emirates ID');
  };

  const handleConfirmRequestName = async () => {
    if (checkingEmiratesId) {
      return;
    }
    const trimmed = customRequestName.trim();
    const digits = trimmed.replace(/\D/g, '');

    setEmiratesGateReason(null);
    setEmiratesGateRequestId(null);

    if (selectedWorkflowLicenseMode === 'normal') {
      if (!trimmed) {
        setRequestNameError(t('requestNameRequired'));
        return;
      }
      if (
        trimmed.length < STUDY_NAME_MIN_LEN ||
        trimmed.length > STUDY_NAME_MAX_LEN
      ) {
        setRequestNameError(
          t('requestNameLengthHint', {
            min: STUDY_NAME_MIN_LEN,
            max: STUDY_NAME_MAX_LEN,
          })
        );
        return;
      }
      setRequestNameError('');
      setShowRequestNameModal(false);
      navigate(
        `/enduser/requests/new-requests/view/${selectedRequestId}?request_name=${encodeURIComponent(trimmed)}`
      );
      return;
    }

    if (!trimmed) {
      setRequestNameError(t('Emirates ID is required'));
      return;
    }
    if (!EMIRATES_ID_DIGITS.test(digits)) {
      setRequestNameError(t('Enter a valid 15-digit Emirates ID'));
      return;
    }

    setRequestNameError('');
    setCheckingEmiratesId(true);
    try {
      const checkRes = await axiosInstance.get(
        '/api/requests/enduser/emirates-id-check',
        {
          params: {
            emirates_id: digits,
            workflow_version_id: selectedRequestId,
          },
          suppressErrorToast: true,
        }
      );
      const {
        allowed,
        reason,
        requests_progress_id: gateRpId,
        request_id: legacyGateId,
      } = checkRes.data || {};
      const gateRp =
        gateRpId != null
          ? String(gateRpId)
          : legacyGateId != null
            ? String(legacyGateId)
            : null;
      if (!allowed) {
        setLastEmiratesDigits(digits);
        setEmiratesGateReason(reason || 'open_existing');
        setEmiratesGateRequestId(gateRp);
        return;
      }

      if (selectedWorkflowLicenseMode === 'license_renewal') {
        await axiosInstance.get('/api/hcp-licenses-list/enduser/renewal-seed', {
          params: { emirates_id: digits },
          suppressErrorToast: true,
        });
        setShowRequestNameModal(false);
        navigate(
          `/enduser/requests/new-requests/view/${selectedRequestId}?request_name=${encodeURIComponent(digits)}`
        );
        return;
      }

      setShowRequestNameModal(false);
      navigate(
        `/enduser/requests/new-requests/view/${selectedRequestId}?request_name=${encodeURIComponent(digits)}`
      );
    } catch (e) {
      if (
        selectedWorkflowLicenseMode === 'license_renewal' &&
        e?.response?.status === 404
      ) {
        setLastEmiratesDigits(digits);
        setEmiratesGateReason('renew_no_completed');
        setEmiratesGateRequestId(null);
      } else {
        setRequestNameError(t('Could not verify Emirates ID'));
      }
    } finally {
      setCheckingEmiratesId(false);
    }
  };

  const handleCancelRequestName = () => {
    setShowRequestNameModal(false);
    setSelectedRequestId(null);
    setCustomRequestName('');
    setRequestNameError('');
    setEmiratesGateReason(null);
    setEmiratesGateRequestId(null);
    setLastEmiratesDigits('');
    setCheckingEmiratesId(false);
    setSelectedWorkflowLicenseMode('new_license');
  };

  const renewLicenseTo =
    emiratesGateReason === 'completed_license' &&
    lastEmiratesDigits &&
    renewalWorkflowVersionId &&
    `/enduser/requests/new-requests/view/${renewalWorkflowVersionId}?request_name=${encodeURIComponent(lastEmiratesDigits)}`;

  const openExistingDetailsTo =
    (emiratesGateReason === 'open_existing' ||
      emiratesGateReason === 'under_review') &&
    emiratesGateRequestId &&
    lastEmiratesDigits &&
    `/enduser/requests/my-requests/details/${emiratesGateRequestId}?request_name=${encodeURIComponent(lastEmiratesDigits)}`;

  const fieldError =
    selectedWorkflowLicenseMode === 'normal'
      ? Boolean(requestNameError)
      : Boolean(requestNameError) || Boolean(emiratesGateReason);

  const modalTitle =
    selectedWorkflowLicenseMode === 'normal'
      ? t('requestNameLabel')
      : t('HCP Emirates ID');

  const inputLabel =
    selectedWorkflowLicenseMode === 'normal'
      ? t('requestNameLabel')
      : t('Please enter an Emirates ID');

  const inputPlaceholder =
    selectedWorkflowLicenseMode === 'normal'
      ? t('requestNamePlaceholder')
      : t('Ex: 123456789012345');

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 32,
          marginTop: 40,
        }}
      >
        {' '}
        {data?.map((item) => (
          <GenericCard
            key={item.id}
            label={item.name}
            description={
              item.description && item.description.length > 150
                ? `${item.description.substring(0, 150)}...`
                : item.description
            }
            buttonLabel={t('Apply')}
            onButtonClick={() => handleApply(item.id)}
            imageId={item?.workflow_image_id}
          />
        ))}
      </div>
      <CommonModal
        open={showRequestNameModal}
        onClose={handleCancelRequestName}
        title={modalTitle}
        size="small"
        buttonProps={[
          {
            label: t('Cancel'),
            onClick: handleCancelRequestName,
          },
          {
            label:
              selectedWorkflowLicenseMode !== 'normal' && checkingEmiratesId
                ? t('Checking...')
                : t('Confirm'),
            onClick: handleConfirmRequestName,
            variant: 'primary',
            disabled:
              selectedWorkflowLicenseMode !== 'normal' && checkingEmiratesId,
          },
        ]}
      >
        <div style={{ overflow: 'visible', minWidth: 400 }}>
          <TextField
            label={inputLabel}
            placeholder={inputPlaceholder}
            value={customRequestName}
            onChange={(e) => {
              setCustomRequestName(e.target.value);
              setRequestNameError('');
              setEmiratesGateReason(null);
              setEmiratesGateRequestId(null);
              setLastEmiratesDigits('');
            }}
            error={fieldError}
            helperText={gateHelperText()}
            fullWidth
            style={{ width: '400px' }}
          />
          {selectedWorkflowLicenseMode !== 'normal' && renewLicenseTo ? (
            <div
              role="alert"
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              <Link
                to={renewLicenseTo}
                style={{ fontWeight: 600, color: '#1565c0' }}
                onClick={(e) => e.stopPropagation()}
              >
                {t('Renew license')}
              </Link>
            </div>
          ) : null}
          {selectedWorkflowLicenseMode !== 'normal' && openExistingDetailsTo ? (
            <div
              role="alert"
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              <Link
                to={openExistingDetailsTo}
                style={{ fontWeight: 600, color: '#1565c0' }}
                onClick={(e) => e.stopPropagation()}
              >
                {t('View existing request')}
              </Link>
            </div>
          ) : null}
        </div>
      </CommonModal>
    </>
  );
};

export default NewRequests;
